import { Suspense, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet } from 'react-router-dom';

import authLayout800 from '@assets/images/auth-layout/auth_layout-800w.webp';
import authLayout1200 from '@assets/images/auth-layout/auth_layout-1200w.webp';
import authLayout1600 from '@assets/images/auth-layout/auth_layout-1600w.webp';
import authLayout2400 from '@assets/images/auth-layout/auth_layout-2400w.webp';
import authLayoutOptimized from '@assets/images/auth-layout/auth_layout-optimized.webp';
import { Spinner } from '@components';
import { useLazyAppConfigQuery } from '@features/authentication/queries/AuthQuery';
import { setAppConfig } from '@layouts/main-header/userSlice';
import MobileLayout from '@layouts/mobile-layout/MobileLayout';

import AccessGuard from '../access-guard/AccessGuard';

const AuthLayout = () => {
  const dispatch = useDispatch();

  const [getAppConfig] = useLazyAppConfigQuery();

  const getAppConfigData = async () => {
    const resp = await getAppConfig(null);
    if ('data' in resp) {
      dispatch(setAppConfig(resp.data));
    }
  };

  useEffect(() => {
    // Defer API call to avoid blocking initial render
    // Use requestIdleCallback for better performance, fallback to setTimeout
    const scheduleConfigLoad = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          () => {
            getAppConfigData();
          },
          { timeout: 2000 }
        );
      } else {
        setTimeout(() => {
          getAppConfigData();
        }, 100);
      }
    };

    scheduleConfigLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AccessGuard>
      <div className="z-100 hidden h-screen w-screen overflow-hidden mobile:flex">
        <MobileLayout />
      </div>
      <div className="flex h-screen items-center mobile:hidden">
        <section className="relative flex h-screen w-full items-center justify-center bg-content py-8 sm:w-1/3 md:w-1/2">
          <picture className="flex size-full items-center justify-center">
            <source
              srcSet={`${authLayout800} 800w, ${authLayout1200} 1200w, ${authLayout1600} 1600w, ${authLayout2400} 2400w`}
              sizes="(max-width: 640px) 33vw, (max-width: 768px) 50vw, 100vw"
              type="image/webp"
            />
            <img
              src={authLayoutOptimized}
              srcSet={`${authLayout800} 800w, ${authLayout1200} 1200w, ${authLayout1600} 1600w, ${authLayout2400} 2400w`}
              sizes="(max-width: 640px) 33vw, (max-width: 768px) 50vw, 100vw"
              alt="login-layout-pic"
              className="max-h-full w-full object-contain"
              fetchPriority="low"
              loading="lazy"
              decoding="async"
            />
          </picture>
        </section>
        <section className="flex w-full items-center justify-center p-10 sm:w-2/3 md:w-1/2">
          <div className="m-6 flex w-105 flex-col justify-start">
            <Suspense
              fallback={
                <div className="flex size-full items-center justify-center">
                  <Spinner />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </div>
        </section>
      </div>
    </AccessGuard>
  );
};

export default AuthLayout;
